import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { connectSocket, disconnectSocket, getSocket } from '@/lib/websocket';

export interface PresenceUser {
  id: string;
  displayName: string;
  avatarUrl?: string;
  color: string;
  joinedAt: Date;
}

interface CollaborationContextValue {
  isConnected: boolean;
  users: PresenceUser[];
  joinRoom: (projectId: string) => void;
  leaveRoom: (projectId: string) => void;
  sendCursorMove: (data: {
    projectId: string;
    sheetId: string;
    rowId: string;
    columnId: string;
  }) => void;
  lockCell: (data: {
    sheetId: string;
    rowId: string;
    columnId: string;
  }) => Promise<boolean>;
  unlockCell: (data: {
    sheetId: string;
    rowId: string;
    columnId: string;
  }) => void;
}

const CollaborationContext = createContext<CollaborationContextValue | null>(null);

interface CollaborationProviderProps {
  userId: string;
  displayName: string;
  children: React.ReactNode;
}

export function CollaborationProvider({
  userId,
  displayName,
  children,
}: CollaborationProviderProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [users, setUsers] = useState<PresenceUser[]>([]);
  const currentRoomRef = useRef<string | null>(null);

  useEffect(() => {
    const socket = connectSocket(userId, displayName);

    socket.on('connect', () => setIsConnected(true));
    socket.on('disconnect', () => setIsConnected(false));

    socket.on('user_joined', (user: PresenceUser) => {
      setUsers((prev) => {
        if (prev.some((u) => u.id === user.id)) return prev;
        return [...prev, { ...user, joinedAt: new Date(user.joinedAt) }];
      });
    });

    socket.on('user_left', (data: { userId: string }) => {
      setUsers((prev) => prev.filter((u) => u.id !== data.userId));
    });

    socket.on('cursor_moved', (_data: unknown) => {
      // Cursor position updates are consumed by grid components directly
    });

    socket.on('cell_locked', (_data: unknown) => {
      // Cell lock state is consumed by grid components directly
    });

    socket.on('cell_unlocked', (_data: unknown) => {
      // Cell unlock state is consumed by grid components directly
    });

    return () => {
      disconnectSocket();
    };
  }, [userId, displayName]);

  const joinRoom = useCallback((projectId: string) => {
    const socket = getSocket();
    if (currentRoomRef.current) {
      socket.emit('leave_room', { projectId: currentRoomRef.current });
    }
    socket.emit('join_room', { projectId });
    currentRoomRef.current = projectId;
  }, []);

  const leaveRoom = useCallback((projectId: string) => {
    const socket = getSocket();
    socket.emit('leave_room', { projectId });
    if (currentRoomRef.current === projectId) {
      currentRoomRef.current = null;
    }
    setUsers([]);
  }, []);

  const sendCursorMove = useCallback(
    (data: { projectId: string; sheetId: string; rowId: string; columnId: string }) => {
      const socket = getSocket();
      socket.emit('cursor_move', data);
    },
    []
  );

  const lockCell = useCallback(
    (data: { sheetId: string; rowId: string; columnId: string }): Promise<boolean> => {
      return new Promise((resolve) => {
        const socket = getSocket();
        socket.emit('cell_lock', data, (response: { success: boolean }) => {
          resolve(response.success);
        });
      });
    },
    []
  );

  const unlockCell = useCallback(
    (data: { sheetId: string; rowId: string; columnId: string }) => {
      const socket = getSocket();
      socket.emit('cell_unlock', data);
    },
    []
  );

  const value: CollaborationContextValue = {
    isConnected,
    users,
    joinRoom,
    leaveRoom,
    sendCursorMove,
    lockCell,
    unlockCell,
  };

  return (
    <CollaborationContext.Provider value={value}>
      {children}
    </CollaborationContext.Provider>
  );
}

export function useCollaborationContext(): CollaborationContextValue {
  const ctx = useContext(CollaborationContext);
  if (!ctx) {
    throw new Error('useCollaborationContext must be used within a CollaborationProvider');
  }
  return ctx;
}
